import { useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '@/components/ui/Icon/Icon';
import { Button } from '@/components/ui/Button/Button';
import { Dialog } from '@/components/ui/Dialog/Dialog';
import { useSession } from '@/services/auth';
import { useUIStore } from '@/stores/uiStore';
import { useKategorier } from '@/services/kategorier';
import { useDeleteVedlikeholdEntry, useVedlikeholdEntries } from '@/services/vedlikehold';
import { nb } from '@/i18n/nb';
import type { VedlikeholdEntry, VedlikeholdKategori } from '@/types/api';
import { EntryFormModal } from './EntryFormModal';
import { KategoriManagementModal } from './KategoriManagementModal';
import { ImageViewerOverlay } from './ImageViewerOverlay';
import s from './Vedlikehold.module.css';

/*
 * Generelt vedlikehold — the public list view + Rediger-mode affordances.
 *
 * Layout (per generelt-public-view.png): entries grouped by kategori, sorted
 * within by updatedAt DESC (server-sorted; we just group + order kategori names).
 * The created-at date is NEVER displayed (technical-spec line 309 — knowledge
 * list, not a chronological log).
 *
 * Affordance visibility (`canEdit`, `canDelete`) is derived server-side per row
 * AND gated client-side on the global editMode flag. The server is authoritative
 * (VedlikeholdService own-or-owner+ check); the UI just hides the buttons.
 */
export function GenereltVedlikehold() {
  const { user } = useSession();
  const editMode = useUIStore((state) => state.editMode);
  const containerRef = useRef<HTMLDivElement>(null);

  const { data: entries, isLoading, isError } = useVedlikeholdEntries('generelt');
  const { data: kategorier } = useKategorier();
  const deleteMutation = useDeleteVedlikeholdEntry();

  // Modal state — kept local rather than in uiStore since these are scoped to
  // this sub-card and won't be opened from elsewhere.
  const [addOpen, setAddOpen] = useState(false);
  const [editEntry, setEditEntry] = useState<VedlikeholdEntry | null>(null);
  const [manageKategorierOpen, setManageKategorierOpen] = useState(false);
  const [deleteCandidate, setDeleteCandidate] = useState<VedlikeholdEntry | null>(null);

  // Move focus into the freshly opened sub-card so keyboard / screen-reader
  // users land on the new content rather than being stranded on the
  // now-unmounted sub-card button (same pattern as Hytteregler sub-card).
  useEffect(() => {
    containerRef.current?.focus();
  }, []);

  // Group entries by kategori_id (server-sorted updatedAt DESC within each).
  // Kategori order is the kategori list's name order, so the layout is stable
  // across renders. Entries with an unknown kategori_id (shouldn't happen for
  // section=generelt) fall under an "Uten kategori" group as a safety net.
  const grouped = useMemo(() => {
    if (!entries || !kategorier) return [];
    const byId = new Map<string, VedlikeholdEntry[]>();
    for (const entry of entries) {
      const key = entry.kategoriId ?? '__none__';
      if (!byId.has(key)) byId.set(key, []);
      byId.get(key)!.push(entry);
    }
    return kategorier
      .map((kategori) => ({ kategori, entries: byId.get(kategori.id) ?? [] }))
      .filter((group) => group.entries.length > 0);
  }, [entries, kategorier]);

  const canContribute = user !== null;
  const showActions = editMode && canContribute;

  function handleDeleteConfirm() {
    if (deleteCandidate === null) return;
    deleteMutation.mutate(
      { id: deleteCandidate.id, section: 'generelt' },
      {
        onSettled: () => setDeleteCandidate(null),
      },
    );
  }

  return (
    <div ref={containerRef} tabIndex={-1} className={s.generelt}>
      {showActions && (
        <div className={s.toolbar}>
          <Button variant="secondary" onClick={() => setManageKategorierOpen(true)}>
            <Icon name="list" size={16} /> {nb.vedlikehold.generelt.manageKategorier}
          </Button>
          <Button variant="primary" onClick={() => setAddOpen(true)}>
            <Icon name="plus" size={16} /> {nb.vedlikehold.generelt.add}
          </Button>
        </div>
      )}

      {isLoading && <p className={s.status} aria-live="polite" />}
      {isError && <p className={s.status}>{nb.vedlikehold.generelt.loadError}</p>}

      {entries && entries.length === 0 && (
        <p className={s.empty}>{nb.vedlikehold.generelt.empty}</p>
      )}

      {grouped.map((group) => (
        <KategoriGroup
          key={group.kategori.id}
          kategori={group.kategori}
          entries={group.entries}
          showActions={showActions}
          onEdit={(entry) => setEditEntry(entry)}
          onDelete={(entry) => setDeleteCandidate(entry)}
        />
      ))}

      {addOpen && (
        <EntryFormModal
          mode="add"
          section="generelt"
          kategorier={kategorier ?? []}
          onClose={() => setAddOpen(false)}
        />
      )}

      {editEntry && (
        <EntryFormModal
          mode="edit"
          section="generelt"
          entry={editEntry}
          kategorier={kategorier ?? []}
          onClose={() => setEditEntry(null)}
        />
      )}

      {manageKategorierOpen && (
        <KategoriManagementModal
          kategorier={kategorier ?? []}
          onClose={() => setManageKategorierOpen(false)}
        />
      )}

      <Dialog
        open={deleteCandidate !== null}
        title={nb.vedlikehold.deleteDialog.title}
        message={nb.vedlikehold.deleteDialog.body}
        confirmLabel={nb.vedlikehold.deleteDialog.confirm}
        cancelLabel={nb.common.cancel}
        confirmVariant="danger"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteCandidate(null)}
      />
    </div>
  );
}

function KategoriGroup({
  kategori,
  entries,
  showActions,
  onEdit,
  onDelete,
}: {
  kategori: VedlikeholdKategori;
  entries: VedlikeholdEntry[];
  showActions: boolean;
  onEdit: (e: VedlikeholdEntry) => void;
  onDelete: (e: VedlikeholdEntry) => void;
}) {
  return (
    <section className={s.kategoriGroup}>
      <header className={s.kategoriHeader}>
        <h2 className={s.kategoriName}>{kategori.name}</h2>
        <span className={s.kategoriCount}>{entries.length}</span>
      </header>
      <div className={s.entries}>
        {entries.map((entry) => (
          <EntryRow
            key={entry.id}
            entry={entry}
            showActions={showActions}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        ))}
      </div>
    </section>
  );
}

function EntryRow({
  entry,
  showActions,
  onEdit,
  onDelete,
}: {
  entry: VedlikeholdEntry;
  showActions: boolean;
  onEdit: (e: VedlikeholdEntry) => void;
  onDelete: (e: VedlikeholdEntry) => void;
}) {
  // GATE-1 D-1 fix: the thumb is a button so it's keyboard-accessible + invokes
  // the viewer overlay (1200² variant). Local state — viewer is per-row.
  const [viewerOpen, setViewerOpen] = useState(false);

  return (
    <article className={s.entry}>
      {entry.image && (
        <>
          <button
            type="button"
            className={s.entryThumbButton}
            onClick={() => setViewerOpen(true)}
            aria-label={nb.vedlikehold.viewer.openAria}
          >
            <img
              className={s.entryThumb}
              src={entry.image.thumbUrl}
              alt={nb.vedlikehold.generelt.imageAlt}
              loading="lazy"
            />
          </button>
          <ImageViewerOverlay
            open={viewerOpen}
            onClose={() => setViewerOpen(false)}
            src={entry.image.viewerUrl}
            alt={entry.description}
          />
        </>
      )}
      <div className={s.entryBody}>
        <p className={s.entryDescription}>{entry.description}</p>
        <div className={s.entryMeta}>
          <span>
            {nb.vedlikehold.generelt.contributedBy} {entry.createdByName}
          </span>
        </div>
        {showActions && (entry.canEdit || entry.canDelete) && (
          <div className={s.entryActions}>
            {entry.canEdit && (
              <button type="button" className={s.entryActionBtn} onClick={() => onEdit(entry)}>
                <Icon name="pencil" size={14} /> {nb.vedlikehold.generelt.edit}
              </button>
            )}
            {entry.canDelete && (
              <button
                type="button"
                className={s.entryActionBtn}
                data-variant="danger"
                onClick={() => onDelete(entry)}
              >
                <Icon name="trash" size={14} /> {nb.vedlikehold.generelt.delete}
              </button>
            )}
          </div>
        )}
      </div>
    </article>
  );
}
